import React, { useEffect, useState } from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import SectionDivider from "../components/SectionDivider";
import RevealOnScroll from "../components/RevealOnScroll";
import ResourceCard from "../components/ResourceCard";
import { BOTANICAL_SRC } from "../components/BotanicalOverlay";
import { PLACEHOLDER_ALCHEMIST_ITEMS } from "../data/alchemistTablePlaceholders";

const API = process.env.REACT_APP_BACKEND_URL;

// Dusty blue / warm blush palette — Nancy Meyers meets Alice in Wonderland.
const DUSTY_BLUE = "#6E8AA8";
const DUSTY_BLUE_DEEP = "#4E6E8C";
const BLUSH = "#D9A3A0";
const BLUSH_SOFT = "#EED6D0";
const CREAM = "#FBF6EF";
const CREAM_SOFT = "#F5EDE3";
const CHARCOAL = "#2C2825";

const ALCHEMIST_PALETTE = {
    surface: "#FFFFFF",
    border: "#E8DDD6",
    thumbBg: BLUSH_SOFT,
    title: DUSTY_BLUE_DEEP,
    body: "rgba(44,40,37,0.78)",
    placeholderText: DUSTY_BLUE,
    buttonBg: DUSTY_BLUE_DEEP,
    buttonText: "#FBF6EF",
    comingSoonText: DUSTY_BLUE,
    comingSoonBorder: BLUSH,
    categoryColors: {
        Reflection: BLUSH,
        Astrology: DUSTY_BLUE,
        Practice: "#B08A6A",
        Tarot: "#8E6E8A",
        Ritual: "#C88B7C",
    },
    fallbackTag: BLUSH,
};

export default function AlchemistsTablePage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetch(`${API}/api/alchemist-table`);
                if (!res.ok) {
                    setItems(PLACEHOLDER_ALCHEMIST_ITEMS);
                    return;
                }
                const data = await res.json();
                setItems(Array.isArray(data) && data.length > 0 ? data : PLACEHOLDER_ALCHEMIST_ITEMS);
            } catch {
                setItems(PLACEHOLDER_ALCHEMIST_ITEMS);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <main data-testid="alchemists-table-page" style={{ background: CREAM, color: CHARCOAL }}>
            <Seo
                title="The Alchemist's Table | Alchemy Practice"
                description="A curated collection of tools, guides, and downloads to support your journey. Reflections, rituals, and resources gathered by Alchemy Practice."
            />
            <Nav />
            <RevealOnScroll>
                <section
                    data-testid="alchemists-table-hero"
                    className="relative pt-40 pb-16 lg:pt-48 lg:pb-20 overflow-hidden"
                    style={{ background: CREAM }}
                >
                    {/* Soft botanical wash on the right edge */}
                    <div
                        aria-hidden
                        className="absolute right-0 top-0 bottom-0 hidden lg:block pointer-events-none"
                        style={{
                            width: "32%",
                            backgroundImage: `url(${BOTANICAL_SRC})`,
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right center",
                            backgroundSize: "contain",
                            opacity: 0.09,
                            mixBlendMode: "multiply",
                            transform: "scaleX(-1)",
                            WebkitMaskImage:
                                "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,1) 100%)",
                            maskImage:
                                "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,1) 100%)",
                        }}
                    />
                    <div className="relative mx-auto max-w-[1100px] px-6 lg:px-12 text-center" data-reveal>
                        <span
                            className="text-[11px] tracking-[0.34em] uppercase font-medium"
                            style={{ color: DUSTY_BLUE, fontFamily: "'DM Sans', sans-serif" }}
                            data-testid="alchemists-table-eyebrow"
                        >
                            The Alchemist&rsquo;s Table
                        </span>
                        <h1
                            className="mt-6 font-serif leading-[1.02] text-balance"
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: "clamp(48px, 6.4vw, 82px)",
                                color: DUSTY_BLUE_DEEP,
                                fontWeight: 500,
                            }}
                            data-testid="alchemists-table-heading"
                        >
                            The Alchemist&rsquo;s{" "}
                            <em
                                style={{
                                    fontStyle: "italic",
                                    color: BLUSH,
                                    fontWeight: 500,
                                }}
                            >
                                Table
                            </em>
                        </h1>
                        <div className="mx-auto mt-8 flex items-center justify-center gap-3" aria-hidden>
                            <span style={{ height: 1, width: 44, background: DUSTY_BLUE, opacity: 0.6 }} />
                            <span style={{ color: BLUSH, fontSize: 14 }}>✦</span>
                            <span style={{ height: 1, width: 44, background: DUSTY_BLUE, opacity: 0.6 }} />
                        </div>
                        <p
                            className="mt-10 max-w-2xl mx-auto text-pretty"
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: "17px",
                                lineHeight: 1.9,
                                color: "rgba(44,40,37,0.82)",
                            }}
                            data-testid="alchemists-table-intro"
                        >
                            A curated collection of tools, guides, and downloads to support your journey.
                            Each piece here was chosen with intention — take what resonates, return when
                            you need it.
                        </p>
                    </div>
                </section>

                <SectionDivider color="rose" bg={CREAM} />

                <section
                    data-testid="alchemists-table-grid-section"
                    className="py-20 lg:py-28"
                    style={{ background: CREAM_SOFT }}
                >
                    <div className="mx-auto max-w-[1280px] px-6 lg:px-12">
                        {loading ? (
                            <p
                                className="text-center italic"
                                style={{ color: DUSTY_BLUE, fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}
                                data-testid="alchemists-table-loading"
                            >
                                Setting the table…
                            </p>
                        ) : items.length === 0 ? (
                            <p
                                className="text-center italic"
                                style={{ color: DUSTY_BLUE, fontFamily: "'Cormorant Garamond', serif", fontSize: 18 }}
                                data-testid="alchemists-table-empty"
                            >
                                New pieces are on their way. Come back soon.
                            </p>
                        ) : (
                            <div
                                className="grid gap-8 lg:gap-10 md:grid-cols-2 lg:grid-cols-3"
                                data-testid="alchemists-table-grid"
                            >
                                {items.map((r) => (
                                    <ResourceCard key={r.id} r={r} palette={ALCHEMIST_PALETTE} />
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                <Footer />
            </RevealOnScroll>
        </main>
    );
}
